import React from 'react';
import {
  Box, Button, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export interface PlanCardPointsFieldProps {
  points: string[];
  // The last line, which the card always shows. It comes from the speeds of
  // the plan, thus it is shown here but cannot be changed here.
  lastPoint: string;
  onChange: (points: string[]) => void;
}

/**
 * The lines with a tick on the signup plan card. The operator can change,
 * add, and remove lines. The typical evening speed is always the last line.
 */
const PlanCardPointsField = ({ points, lastPoint, onChange }: PlanCardPointsFieldProps) => {
  const setPoint = (index: number, value: string) => (
    onChange(points.map((point, i) => (i === index ? value : point)))
  );
  const removePoint = (index: number) => onChange(points.filter((_, i) => i !== index));

  return (
    <Box role="group" aria-label="Plan card points">
      <Typography variant="subtitle2">Plan card points</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        The lines with a tick on the signup plan card, in this sequence. The
        speed is always the last line.
      </Typography>
      <Stack spacing={1.5}>
        {points.map((point, i) => {
          const empty = point.trim() === '';
          return (
            // The lines have no id of their own, thus the key is the position.
            // eslint-disable-next-line react/no-array-index-key
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label={`Point ${i + 1}`}
                value={point}
                onChange={(e) => setPoint(i, e.target.value)}
                error={empty}
                helperText={empty ? 'Write the point, or remove it' : undefined}
                size="small"
                fullWidth
              />
              <Tooltip title="Remove this point">
                <IconButton aria-label={`Remove point ${i + 1}`} onClick={() => removePoint(i)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
        <TextField
          label="Last point (always shown)"
          value={lastPoint}
          disabled
          size="small"
          helperText={'The typical evening speed for the access technology of the building. '
            + 'If the evening speeds are not set or are the same as the plan speed, it is '
            + '"Max Theoretical speed#" with the plan speed, and the # is in superscript on '
            + 'the card. The preview uses the passive (GPON) pair.'}
          fullWidth
        />
        <Box>
          <Button startIcon={<AddIcon />} onClick={() => onChange([...points, ''])}>
            Add a point
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default PlanCardPointsField;
